import React, { useState } from "react";
import styled from "styled-components";
import { color } from "styles/colours";
import { Image, Text, types, File, RichText, Repeater } from "react-bricks/frontend";
import { H2 } from "components/atoms/Heading";
import { P1 } from "components/atoms/Paragraph";
import { VideoPlayer } from "components/atoms/VideoPlayer";
import { mobile } from "components/templates/Breakpoints";
import {
  getVerticalPadding,
  VerticalPadding,
  VerticalPaddingWrapper,
  verticalPadSideProp,
} from "../utils/verticalPadding";
import LottieUploadedImage from "../common/LottieUploadedImage";
import { PlayButton } from "components/atoms/PlayButton";
import { Vimeo } from "@services";
import { backgroundColorProp } from "../utils/colorPickerProps";

export interface TextWithImageItemProps {
  reverse: boolean;
  /** @deprecated - use imageType instead */
  useVideo?: boolean;
  mediaType?: "image" | "video" | "lottie" | "vimeo";
  paddingVertical?: VerticalPadding;
  backgroundColor: string;
  videoUrl: string;
  maxWidth?: number;
  vimeoId?: string;
  vimeoAltText?: string;
}

export const TextWithImage: types.Brick<TextWithImageItemProps> = ({
  reverse = false,
  useVideo = false,
  paddingVertical,
  backgroundColor,
  mediaType = useVideo ? "video" : "image",
  videoUrl,
  vimeoId,
  vimeoAltText,
  maxWidth,
}) => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  return (
    <VerticalPaddingWrapper paddingVertical={paddingVertical} backgroundColor={backgroundColor}>
      <TextWithImageContainer reverse={reverse}>
        <TextContainer>
          <Repeater propName="copyBlock" />
        </TextContainer>

        <ImageContainer maxWidth={maxWidth}>
          {mediaType === "video" && (
            <File
              propName="videoUrl"
              allowedExtensions={[".mp4"]}
              renderBlock={(props) => (
                <VideoWrapper selected={!!videoUrl}>
                  <VideoPlayer mp4={props?.url} $maxWidth="544px" />
                </VideoWrapper>
              )}
            />
          )}

          {mediaType === "image" && (
            <Image
              propName="image"
              alt={"image"}
              maxWidth={maxWidth || 500}
              imageStyle={{ maxWidth, borderRadius: 8 }}
            />
          )}

          {mediaType === "lottie" && (
            <LottieUploadedImage propName="lottieUrl" maxWidth={`${maxWidth || "500"}px`} />
          )}

          {mediaType === "vimeo" && (
            <>
              {isVideoOpen && (
                <VimeoWrapper>
                  <Vimeo
                    vimeoId={vimeoId || ""}
                    isOpen={isVideoOpen}
                    onClose={() => setIsVideoOpen(false)}
                  />
                </VimeoWrapper>
              )}

              <PreviewWrapper>
                <Image
                  propName="videoImg"
                  alt={vimeoAltText || "Video preview"}
                  maxWidth={maxWidth || 544}
                />
                <PlayButtonOverlay onClick={() => setIsVideoOpen(true)}>
                  <PlayButton />
                </PlayButtonOverlay>
              </PreviewWrapper>
            </>
          )}
        </ImageContainer>
      </TextWithImageContainer>
    </VerticalPaddingWrapper>
  );
};

TextWithImage.schema = {
  name: "zigzag",
  label: "Text with Image",
  previewImageUrl: "/static/bricks/zigzag.png",
  getDefaultProps: () => ({
    verticalPadding: "s",
    backgroundColor: color.white,
    image: {
      src: "https://assets.reactbricks.com/wqzbqeh9bMnAhYE/images/original/Au2Awpxyb4CFVLP/today-screen.webp",
    },
  }),
  repeaterItems: [
    {
      name: "copyBlock",
      itemType: "text-with-heading-block",
      itemLabel: "Copy",
      min: 1,
      max: 1,
    },
  ],
  sideEditProps: [
    {
      name: "reverse",
      label: "Reverse layout",
      type: types.SideEditPropType.Boolean,
      shouldRefreshStyles: true,
    },
    backgroundColorProp,
    {
      name: "mediaType",
      label: "Media Type",
      type: types.SideEditPropType.Select,
      shouldRefreshStyles: true,
      selectOptions: {
        display: types.OptionsDisplay.Select,
        options: [
          { value: "image", label: "Image" },
          { value: "video", label: "Video" },
          { value: "lottie", label: "Lottie" },
          { value: "vimeo", label: "Vimeo" },
        ],
      },
    },
    {
      name: "vimeoId",
      label: "Vimeo ID",
      type: types.SideEditPropType.Text,
      show: (props) => props.mediaType === "vimeo",
    },
    {
      name: "vimeoAltText",
      label: "Vimeo Alt Text",
      type: types.SideEditPropType.Text,
      show: (props) => props.mediaType === "vimeo",
    },
    {
      name: "videoImg",
      label: "Vimeo Preview Image",
      type: types.SideEditPropType.Image,
      show: (props) => props.mediaType === "vimeo",
    },
    {
      name: "maxWidth",
      label: "Max Media Width",
      type: types.SideEditPropType.Range,
      shouldRefreshStyles: true,
      rangeOptions: {
        min: 200,
        max: 800,
        step: 50,
      },
    },
    verticalPadSideProp,
  ],
};

const ZIG_ZAG_BREAKPOINT = `@media only screen and (max-width: 76rem)`;

const TextWithImageContainer = styled.div<{
  reverse: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8rem;
  flex-direction: ${(p) => (p.reverse ? "row-reverse" : "row")};

  ${ZIG_ZAG_BREAKPOINT} {
    flex-direction: column;
    gap: 3rem;
    align-items: center;

    padding-left: 2rem;
    padding-right: 2rem;
  }
`;

const TextContainer = styled.div`
  width: 28rem;

  ${ZIG_ZAG_BREAKPOINT} {
    width: 34rem;
  }

  ${mobile} {
    width: 100%;
  }
`;

const ImageContainer = styled.div<{ maxWidth: number }>`
  width: 34rem;
  max-width: ${(p) => (p.maxWidth ? `${p.maxWidth}px` : "none")};

  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  @media only screen and (max-width: 36rem) {
    width: 100%;
    height: auto;
  }
`;

const VideoWrapper = styled.div<{ selected: boolean }>`
  height: 30rem;
  width: 100%;
  background-color: ${(p) => (p.selected ? "transparent" : color.B1100)};
  border-radius: 0.5rem;
  overflow: hidden;

  ${mobile} {
    height: auto;
  }
`;

const PreviewWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;

  img {
    width: 100%;
    height: auto;
    border-radius: 8px;
  }
`;

const PlayButtonOverlay = styled.div`
  display: flex;
  position: absolute;
  top: 42%;
  left: 43%;
  cursor: pointer;
  max-width: 11%;
`;

const VimeoWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
`;
